import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { cn } from '@/utils/cn';

const DIRECTORY_LINKS = [
  { to: '/people', end: true, key: 'nav.members' },
  { to: '/people/families', end: false, key: 'nav.families' },
  { to: '/people/birthdays', end: false, key: 'nav.birthdays' },
  { to: '/people/anniversaries', end: false, key: 'nav.anniversaries' },
] as const;

const WORKFLOW_LINKS = [
  {
    to: '/people/join-requests',
    end: false,
    key: 'nav.joinRequests',
    countKey: 'join-requests' as const,
    permission: PERMISSIONS.PEOPLE_VIEW,
  },
  {
    to: '/people/follow-ups',
    end: false,
    key: 'nav.followUps',
    countKey: 'follow-ups-overdue' as const,
    permission: PERMISSIONS.FOLLOWUP_VIEW,
  },
] as const;

const LEADERSHIP_LINK = {
  to: '/people/departments',
  end: false,
  key: 'nav.leadership',
  permission: PERMISSIONS.PEOPLE_MANAGE_DEPARTMENTS,
} as const;

export const PeopleSubnav = () => {
  const { t } = useTranslation('people');
  const membership = useCurrentMembership();

  const workflowLinks = WORKFLOW_LINKS.filter(
    (link) => !('permission' in link) || hasPermission(membership, link.permission),
  );
  const showLeadership =
    !('permission' in LEADERSHIP_LINK) || hasPermission(membership, LEADERSHIP_LINK.permission);

  const pendingJoinRequests = useQuery({
    queryKey: ['people', 'join-requests', 'pending-count'],
    queryFn: async () => {
      const res = await api.get<{
        page: { totalItems: number };
      }>('/people/join-requests?page=1&pageSize=1&status=pending');
      return res.page.totalItems;
    },
    staleTime: 30_000,
    enabled: workflowLinks.some(
      (link) => 'countKey' in link && link.countKey === 'join-requests',
    ),
  });

  const overdueFollowUps = useQuery({
    queryKey: ['people', 'follow-ups', 'overdue-count'],
    queryFn: async () => {
      const res = await api.get<{
        page: { totalItems: number };
      }>('/people/follow-ups?page=1&pageSize=1&overdue=true');
      return res.page.totalItems;
    },
    staleTime: 30_000,
    enabled: workflowLinks.some(
      (link) => 'countKey' in link && link.countKey === 'follow-ups-overdue',
    ),
  });

  const renderLink = (link: (typeof DIRECTORY_LINKS)[number] | (typeof WORKFLOW_LINKS)[number]) => {
    const pendingCount =
      'countKey' in link && link.countKey === 'join-requests'
        ? (pendingJoinRequests.data ?? 0)
        : 'countKey' in link && link.countKey === 'follow-ups-overdue'
          ? (overdueFollowUps.data ?? 0)
          : 0;

    return (
      <NavLink
        key={link.to}
        to={link.to}
        end={link.end}
        className={({ isActive }) =>
          cn(
            'rounded-md px-3 py-1.5 text-body-sm',
            isActive ? 'bg-brand/10 text-brand' : 'text-fg-secondary hover:bg-surface-sunken',
          )
        }
      >
        <span className="inline-flex items-center gap-1.5">
          {t(link.key)}
          {pendingCount > 0 ? (
            <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-brand px-1.5 py-0.5 text-caption font-medium text-on-brand">
              {pendingCount > 99 ? '99+' : pendingCount}
            </span>
          ) : null}
        </span>
      </NavLink>
    );
  };

  return (
    <nav className="mb-6 flex flex-wrap items-center gap-2" aria-label={t('nav.members')}>
      {DIRECTORY_LINKS.map(renderLink)}
      {workflowLinks.length > 0 ? (
        <span className="hidden h-5 w-px bg-border sm:inline-block" aria-hidden />
      ) : null}
      {workflowLinks.map(renderLink)}
      {showLeadership ? (
        <>
          <span className="hidden h-5 w-px bg-border sm:inline-block" aria-hidden />
          <NavLink
            to={LEADERSHIP_LINK.to}
            end={LEADERSHIP_LINK.end}
            className={({ isActive }) =>
              cn(
                'rounded-md px-3 py-1.5 text-body-sm',
                isActive ? 'bg-brand/10 text-brand' : 'text-fg-secondary hover:bg-surface-sunken',
              )
            }
          >
            {t(LEADERSHIP_LINK.key)}
          </NavLink>
        </>
      ) : null}
    </nav>
  );
};
